/**
 * Combined Donut/Pill Chart Component
 * Outer ring: dimension with fewer unique values (donut)
 * Inner chart: dimension with more unique values (horizontal pill bar with segments)
 */

import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { safeParseNumber } from '../utils/numberUtils';

interface DonutSunburstChartProps {
  outerData: Record<string, number>; // e.g., remoteness: { remote: 10, hybrid: 5, onsite: 3 }
  innerData: Record<string, number>; // e.g., locations: { Helsinki: 8, Espoo: 5, Tampere: 3, ... }
  outerTitle: string;
  innerTitle: string;
  brandColour?: string;
  companyName?: string;
  locale?: string;
  className?: string;
  maxInnerItems?: number;
  minOuterPercentage?: number; // Only show outer donut items >= this percentage (default: 3)
  outerCaption?: React.ReactNode; // Caption for outer donut
  innerCaption?: React.ReactNode; // Caption for inner pill
}

export const DonutSunburstChart: React.FC<DonutSunburstChartProps> = memo(({
  outerData,
  innerData,
  outerTitle,
  innerTitle,
  brandColour,
  companyName,
  locale = 'en',
  className = '',
  maxInnerItems = 12,
  minOuterPercentage = 3,
  outerCaption,
  innerCaption,
}) => {
  const intl = useIntl();
  
  // Filter and validate outer data
  const allOuterEntries = Object.entries(outerData)
    .map(([label, value]) => [label, safeParseNumber(value)] as [string, number | null])
    .filter(([, value]) => value !== null && value > 0) as Array<[string, number]>;
  
  // Filter and validate inner data
  const innerEntries = Object.entries(innerData)
    .map(([label, value]) => [label, safeParseNumber(value)] as [string, number | null])
    .filter(([, value]) => value !== null && value > 0) as Array<[string, number]>;
  
  if (allOuterEntries.length === 0 || innerEntries.length === 0) {
    return null;
  }
  
  // Calculate total from ALL outer entries (before limiting)
  const outerTotal = allOuterEntries.reduce((sum, [, value]) => sum + value, 0);
  
  // Filter outer items to only show those >= minOuterPercentage of total
  const sortedOuterEntries = allOuterEntries
    .filter(([, value]) => (value / outerTotal) * 100 >= minOuterPercentage)
    .sort(([, a], [, b]) => b - a);
  
  const isOuterLimited = allOuterEntries.length > sortedOuterEntries.length;
  
  // Sort and limit inner items
  const sortedInnerEntries = innerEntries
    .sort(([, a], [, b]) => b - a)
    .slice(0, maxInnerItems);
  
  // Calculate totals
  const innerTotal = sortedInnerEntries.reduce((sum, [, value]) => sum + value, 0);
  
  // Chart dimensions - increased to accommodate outer labels
  const width = 500;
  const height = 500;
  const padding = 60; // Extra padding for outer labels
  const centerX = width / 2;
  const centerY = height / 2;
  
  // Outer donut dimensions
  const outerRadius = 180;
  const outerInnerRadius = 120; // Inner radius of outer donut (where pill starts)
  
  // Inner pill chart dimensions - positioned inside the donut
  const pillBarHeight = 20; // Height of the pill bar
  const pillBarWidth = (outerInnerRadius - 20) * 1.6; // Width of the pill bar
  const pillX = centerX - pillBarWidth / 2;
  const pillY = centerY - pillBarHeight / 2;
  const centerCircleRadius = 20; // Small center circle
  const centerCircleY = pillY - centerCircleRadius - 10; // Position above the pill
  
  // Color palette
  const primaryColor = brandColour || '#3b82f6';
  const baseColors = ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7', '#f43f5e'];
  const colors = [primaryColor, ...baseColors];
  
  // Calculate outer donut segments (using limited entries for display, but percentages based on full total)
  let outerCurrentAngle = -Math.PI / 2; // Start at top
  const outerSegments = sortedOuterEntries.map(([label, value], index) => {
    const percentage = (value / outerTotal) * 100;
    const angle = (value / outerTotal) * (2 * Math.PI);
    
    const startAngle = outerCurrentAngle;
    const endAngle = outerCurrentAngle + angle;
    
    // Calculate arc path for donut segment
    const largeArcFlag = angle > Math.PI ? 1 : 0;
    
    const x1 = centerX + outerRadius * Math.cos(startAngle);
    const y1 = centerY + outerRadius * Math.sin(startAngle);
    const x2 = centerX + outerRadius * Math.cos(endAngle);
    const y2 = centerY + outerRadius * Math.sin(endAngle);
    
    const x3 = centerX + outerInnerRadius * Math.cos(startAngle);
    const y3 = centerY + outerInnerRadius * Math.sin(startAngle);
    const x4 = centerX + outerInnerRadius * Math.cos(endAngle);
    const y4 = centerY + outerInnerRadius * Math.sin(endAngle);
    
    // Create path for donut segment
    const path = [
      `M ${x1} ${y1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x4} ${y4}`,
      `A ${outerInnerRadius} ${outerInnerRadius} 0 ${largeArcFlag} 0 ${x3} ${y3}`,
      'Z',
    ].join(' ');
    
    // Label position (middle of arc, outside the donut)
    const labelAngle = startAngle + angle / 2;
    const labelRadius = outerRadius + 20; // Slightly closer to donut
    const labelX = centerX + labelRadius * Math.cos(labelAngle);
    const labelY = centerY + labelRadius * Math.sin(labelAngle);
    
    // Line start position (on the outer edge of the donut)
    const lineStartX = centerX + outerRadius * Math.cos(labelAngle);
    const lineStartY = centerY + outerRadius * Math.sin(labelAngle);
    
    // Line end position (just before the label)
    const lineEndX = centerX + (outerRadius + 15) * Math.cos(labelAngle);
    const lineEndY = centerY + (outerRadius + 15) * Math.sin(labelAngle);
    
    // Percentage position (middle of arc, on the donut segment)
    const percentageRadius = (outerRadius + outerInnerRadius) / 2; // Middle of donut ring
    const percentageX = centerX + percentageRadius * Math.cos(labelAngle);
    const percentageY = centerY + percentageRadius * Math.sin(labelAngle);
    
    // Text anchor based on angle
    let textAnchor: 'start' | 'middle' | 'end' = 'middle';
    if (labelAngle > -Math.PI / 2 && labelAngle < Math.PI / 2) {
      textAnchor = 'start';
    } else {
      textAnchor = 'end';
    }
    
    outerCurrentAngle += angle;
    
    return {
      label,
      value,
      percentage: percentage.toFixed(1),
      path,
      color: colors[index % colors.length],
      labelX,
      labelY,
      textAnchor,
      midAngle: labelAngle,
      percentageX,
      percentageY,
      lineStartX,
      lineStartY,
      lineEndX,
      lineEndY,
    };
  });
  
  // Calculate inner pill chart - single horizontal pill bar with segments
  // Calculate segment positions (stacked horizontally)
  let currentX = pillX;
  const innerSegments = sortedInnerEntries.map(([label, value], index) => {
    const percentage = (value / innerTotal) * 100;
    const segmentWidth = (value / innerTotal) * pillBarWidth;
    
    const segmentX = currentX;
    const segmentY = pillY;
    
    // Percentage label position - center of segment on the pill
    const percentageX = segmentX + segmentWidth / 2;
    const percentageY = pillY + pillBarHeight / 2;
    
    // Text label position - starts at the bottom of the pill, rotated 90 degrees to write downwards
    const labelX = segmentX + segmentWidth / 2;
    const labelY = pillY + pillBarHeight + 5; // Start just below the pill
    
    // Move to next segment
    currentX += segmentWidth;
    
    return {
      label,
      value,
      percentage: percentage.toFixed(1),
      segmentX,
      segmentY,
      segmentWidth,
      segmentHeight: pillBarHeight,
      color: colors[(index + sortedOuterEntries.length) % colors.length],
      percentageX,
      percentageY,
      labelX,
      labelY,
    };
  });
  
  return (
    <div className={`donut-sunburst-chart ${className} flex flex-col items-center relative`}>
      {/* Outer caption above - overlapping SVG */}
      {outerCaption && (
        <div className="w-full mb-0 relative z-10 bg-white dark:bg-gray-900 px-2 rounded-lg" style={{ marginBottom: '-60px', marginTop: '0' }}>
          {outerCaption}
        </div>
      )}
      
      <div className="flex flex-col items-center justify-center w-full relative">
        <svg width={width} height={height} className="w-full max-w-[500px] mx-auto block" style={{ display: 'block' }} viewBox={`-${padding} -${padding} ${width + padding * 2} ${height + padding * 2}`}>
          {/* Outer donut segments */}
          {outerSegments.map((segment, index) => (
            <g key={`outer-${index}`}>
              <path
                d={segment.path}
                fill={segment.color}
                opacity="0.8"
                stroke="#fff"
                strokeWidth="2"
                className="dark:stroke-gray-800"
              />
              {/* Connecting line from donut to label */}
              <line
                x1={segment.lineStartX}
                y1={segment.lineStartY}
                x2={segment.lineEndX}
                y2={segment.lineEndY}
                stroke="currentColor"
                strokeWidth="1"
                opacity="0.2"
                className="text-gray-500 dark:text-gray-400"
              />
              {/* Percentage on donut segment */}
              <text
                x={segment.percentageX}
                y={segment.percentageY}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-white font-semibold"
                fontSize="9"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
              >
                {segment.percentage}%
              </text>
              {/* Outer labels */}
              <text
                x={segment.labelX}
                y={segment.labelY}
                textAnchor={segment.textAnchor}
                dominantBaseline="middle"
                className="text-xs fill-gray-700 dark:fill-gray-200 font-medium"
                fontSize="12"
              >
                {segment.label}
              </text>
            </g>
          ))}
          
          {/* Inner pill bar - single horizontal pill with segments */}
          <g>
            {/* Background pill */}
            <rect
              x={pillX}
              y={pillY}
              width={pillBarWidth}
              height={pillBarHeight}
              rx={pillBarHeight / 2}
              fill="currentColor"
              className="text-gray-100 dark:text-gray-800"
            />
            {/* Segments */}
            {innerSegments.map((segment, index) => {
              const isFirst = index === 0;
              const isLast = index === innerSegments.length - 1;
              const radius = pillBarHeight / 2;
              
              // Create path for segment with rounded corners only on ends
              let path = '';
              if (isFirst && isLast) {
                // Only one segment - fully rounded pill
                path = `M ${segment.segmentX + radius} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth - radius} ${segment.segmentY}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + radius}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + segment.segmentHeight - radius}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + segment.segmentWidth - radius} ${segment.segmentY + segment.segmentHeight}
                        L ${segment.segmentX + radius} ${segment.segmentY + segment.segmentHeight}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX} ${segment.segmentY + segment.segmentHeight - radius}
                        L ${segment.segmentX} ${segment.segmentY + radius}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + radius} ${segment.segmentY}
                        Z`;
              } else if (isFirst) {
                // First segment - rounded left side only
                path = `M ${segment.segmentX + radius} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + segment.segmentHeight}
                        L ${segment.segmentX + radius} ${segment.segmentY + segment.segmentHeight}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX} ${segment.segmentY + segment.segmentHeight - radius}
                        L ${segment.segmentX} ${segment.segmentY + radius}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + radius} ${segment.segmentY}
                        Z`;
              } else if (isLast) {
                // Last segment - rounded right side only
                path = `M ${segment.segmentX} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth - radius} ${segment.segmentY}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + radius}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + segment.segmentHeight - radius}
                        A ${radius} ${radius} 0 0 1 ${segment.segmentX + segment.segmentWidth - radius} ${segment.segmentY + segment.segmentHeight}
                        L ${segment.segmentX} ${segment.segmentY + segment.segmentHeight}
                        Z`;
              } else {
                // Middle segments - straight rectangle
                path = `M ${segment.segmentX} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY}
                        L ${segment.segmentX + segment.segmentWidth} ${segment.segmentY + segment.segmentHeight}
                        L ${segment.segmentX} ${segment.segmentY + segment.segmentHeight}
                        Z`;
              }
              
              return (
                <g key={`inner-${index}`}>
                  {/* Segment */}
                  <path
                    d={path}
                    fill={segment.color}
                    opacity="0.9"
                  />
                  {/* Percentage on the pill - only show if segment is wide enough */}
                  {segment.segmentWidth > 30 && (
                    <text
                      x={segment.percentageX}
                      y={segment.percentageY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-white font-semibold"
                      fontSize="8"
                      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                    >
                      {segment.percentage}%
                    </text>
                  )}
                {/* Label below the pill - rotated diagonally to write downwards */}
                <text
                  x={segment.labelX}
                  y={segment.labelY}
                  textAnchor="start"
                  dominantBaseline="hanging"
                  className="text-xs fill-gray-700 dark:text-gray-300 font-medium"
                  fontSize="9"
                  transform={`rotate(60 ${segment.labelX} ${segment.labelY})`}
                >
                  {segment.label}
                </text>
                </g>
              );
            })}
          </g>
          
          {/* Center circle - positioned above the pill */}
          <circle
            cx={centerX}
            cy={centerCircleY}
            r={centerCircleRadius}
            fill="#fff"
            stroke="#e5e7eb"
            strokeWidth="2"
            className="dark:fill-gray-800 dark:stroke-gray-700"
          />
          <text
            x={centerX}
            y={centerCircleY}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-xs fill-gray-600 dark:fill-gray-300 font-semibold"
            fontSize="12"
          >
            {innerTotal}
          </text>
        </svg>
      </div>
      
      {/* Inner caption below - overlapping SVG */}
      {innerCaption && (
        <div className="w-full mt-0 relative z-10 bg-white dark:bg-gray-900 px-2 rounded-lg" style={{ marginTop: '-60px', marginBottom: '0' }}>
          {innerCaption}
        </div>
      )}
      
      {/* Disclaimer when outer data is limited */}
      {isOuterLimited && (
        <div className="w-full mt-2 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
            <FormattedMessage
              id="jobDetailPage.showingItemsAboveThreshold"
              defaultMessage="Showing only items above {percentage}%"
              description="Message indicating chart is filtered to show only items above a certain percentage threshold, where {percentage} is the threshold value"
              values={{ percentage: minOuterPercentage }}
            />
          </p>
        </div>
      )}
    </div>
  );
});

DonutSunburstChart.displayName = 'DonutSunburstChart';
